import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { ACCOMMODATIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQ = <span className="text-sa-accent ml-0.5" aria-hidden="true">*</span>;

export default function ContactForm() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => {
    const stayId = params.get("stay") || params.get("package");
    const matched = stayId ? ACCOMMODATIONS.find((a) => a.id === stayId) : null;
    const paramAdults = Number(params.get("adults"));
    const paramChildren = Number(params.get("children"));
    return {
      name: "",
      email: "",
      phone: "",
      accommodation_type: matched ? matched.name : "",
      check_in: params.get("check_in") || "",
      check_out: params.get("check_out") || "",
      adults: Number.isFinite(paramAdults) && paramAdults > 0 ? paramAdults : 2,
      children: Number.isFinite(paramChildren) && paramChildren >= 0 ? paramChildren : 0,
      has_pet: false,
      message: params.get("message") || (matched
        ? `Hi Shady Acres team, I'd like to book a ${matched.name}.`
        : ""),
    };
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...form,
        adults: Number(form.adults),
        children: Number(form.children),
        has_pet: Boolean(form.has_pet),
      });
      toast.success("Enquiry sent — we'll be in touch within the hour.");
      setForm({
        name: "", email: "", phone: "", accommodation_type: "",
        check_in: "", check_out: "", adults: 2, children: 0,
        has_pet: false, message: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cls = "h-12 w-full bg-white border border-sa-ink/15 rounded-md px-4 text-sa-ink placeholder:text-sa-ink2/60 focus:border-sa-ink focus:outline-none transition-colors";

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
      <div className="md:col-span-1">
        <label className="overline block mb-2">Name{REQ}</label>
        <input required name="name" value={form.name} onChange={onChange} className={cls} placeholder="Your name" data-testid="enquiry-name" />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Email{REQ}</label>
        <input required type="email" name="email" value={form.email} onChange={onChange} className={cls} placeholder="you@email.com" data-testid="enquiry-email" />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Phone{REQ}</label>
        <input required name="phone" value={form.phone} onChange={onChange} className={cls} placeholder="+61 4XX XXX XXX" data-testid="enquiry-phone" />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Stay type{REQ}</label>
        <select required name="accommodation_type" value={form.accommodation_type} onChange={onChange} className={cls} data-testid="enquiry-type">
          <option value="" disabled>Select a stay type…</option>
          {ACCOMMODATIONS.map((a) => (
            <option key={a.id} value={a.name}>{a.name}</option>
          ))}
          <option value="Long-term / FIFO">Long-term / FIFO</option>
        </select>
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Check-in{REQ}</label>
        <input required type="date" name="check_in" value={form.check_in} onChange={onChange} className={cls} data-testid="enquiry-checkin" />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Check-out{REQ}</label>
        <input required type="date" name="check_out" value={form.check_out} onChange={onChange} className={cls} data-testid="enquiry-checkout" />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Guests (Adults){REQ}</label>
        <input required type="number" min={1} max={12} name="adults" value={form.adults} onChange={onChange} className={cls} data-testid="enquiry-adults" />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-2">Guests (Children){REQ}</label>
        <input required type="number" min={0} max={12} name="children" value={form.children} onChange={onChange} className={cls} data-testid="enquiry-children" />
      </div>
      <div className="md:col-span-2 flex items-center gap-3 pt-1">
        <input
          id="enquiry-has-pet"
          type="checkbox"
          name="has_pet"
          checked={form.has_pet}
          onChange={onChange}
          className="h-5 w-5 rounded border-sa-ink/30 text-sa-accent focus:ring-sa-accent accent-sa-accent cursor-pointer"
          data-testid="enquiry-has-pet"
        />
        <label htmlFor="enquiry-has-pet" className="text-sm text-sa-ink cursor-pointer select-none">
          Bringing a pet? Tick if yes — we&apos;ll match you with a pet-friendly site or cabin.
        </label>
      </div>
      <div className="md:col-span-2">
        <label className="overline block mb-2">Message{REQ}</label>
        <textarea
          required
          name="message"
          value={form.message}
          onChange={onChange}
          rows={5}
          className="w-full bg-white border border-sa-ink/15 rounded-md px-4 py-3 text-sa-ink placeholder:text-sa-ink2/60 focus:border-sa-ink focus:outline-none transition-colors"
          placeholder="Tell us a bit about your trip — pets, vehicle size, special requests…"
          data-testid="enquiry-message"
        />
      </div>
      <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 pt-2">
        <p className="text-xs text-sa-ink2 max-w-md">
          <span className="text-sa-accent">*</span> All fields required. We respond to enquiries within the hour, Mon–Sun. By submitting, you agree to our{" "}
          <Link to="/policies#terms" className="text-sa-accent hover:underline" data-testid="enquiry-terms-link">Terms &amp; Conditions</Link>
          {" "}and{" "}
          <Link to="/policies#privacy" className="text-sa-accent hover:underline" data-testid="enquiry-privacy-link">Privacy Policy</Link>.
        </p>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary disabled:opacity-50"
          data-testid="enquiry-submit"
        >
          {loading ? "Sending…" : "Send enquiry"} <PaperPlaneTilt size={16} weight="bold" />
        </button>
      </div>
    </form>
  );
}
